import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  X, History, CheckCircle2, RotateCcw, Search, Calendar, Loader2, Info, ExternalLink,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const money = (v) => `R$ ${(Number(v) || 0).toFixed(2).replace('.', ',')}`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const parts = iso.split('T')[0].split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  } catch { return '—'; }
};

/**
 * Full-screen modal listing every project with its financial info + payment_status
 * toggle. Only Dono/whoever has `access_payments_page` can toggle status.
 */
const ProjectHistoryModal = ({ open, onClose, canToggle }) => {
  const { getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [forecast, setForecast] = useState({});  // project_id → full forecast entry
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'nao_pago' | 'pago'
  const [updating, setUpdating] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null); // project id being detailed

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRes, histRes, fc] = await Promise.all([
        axios.get(`${API}/projects?status_filter=active`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects?status_filter=history`, { headers: getAuthHeaders() }),
        axios.get(`${API}/finance-dashboard/forecast?status_filter=todos`, { headers: getAuthHeaders() }),
      ]);
      const all = [...(activeRes.data || []), ...(histRes.data || [])];
      const seen = new Set();
      const unique = all.filter(p => (seen.has(p.id) ? false : (seen.add(p.id), true)));
      setProjects(unique);
      const fmap = {};
      (fc.data?.projects || []).forEach(p => {
        fmap[p.project_id] = p;
      });
      setForecast(fmap);
    } catch {
      toast.error('Erro ao carregar histórico de projetos');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const toggleStatus = async (project) => {
    const next = project.payment_status === 'pago' ? 'nao_pago' : 'pago';
    setUpdating(project.id);
    try {
      await axios.patch(`${API}/projects/${project.id}/payment-status`,
        { payment_status: next },
        { headers: getAuthHeaders() },
      );
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, payment_status: next } : p));
      toast.success(next === 'pago' ? 'Projeto marcado como PAGO.' : 'Projeto reaberto (não pago).');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar status.');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return projects
      .filter(p => {
        if (statusFilter !== 'all' && (p.payment_status || 'nao_pago') !== statusFilter) return false;
        if (!q) return true;
        return (p.title || '').toLowerCase().includes(q)
          || (p.client || '').toLowerCase().includes(q);
      })
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [projects, search, statusFilter]);

  if (!open) return null;

  const totalPaid = projects.filter(p => p.payment_status === 'pago')
    .reduce((s, p) => s + (forecast[p.id]?.total || 0), 0);
  const totalOpen = projects.filter(p => (p.payment_status || 'nao_pago') !== 'pago')
    .reduce((s, p) => s + (forecast[p.id]?.total || 0), 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="project-history-modal"
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden relative"
        onClick={e => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-700 flex items-start justify-between gap-3 shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <History className="h-5 w-5 text-amber-400" />
              Histórico de Projetos
            </h2>
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <Info className="h-3 w-3" />
              Projetos marcados como PAGO não aparecem mais nos modais de criação de pagamento.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white"
            data-testid="project-history-close"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Filters + totals */}
        <div className="px-6 py-3 border-b border-slate-800 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar projeto ou cliente…"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
              data-testid="project-history-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-amber-500"
            data-testid="project-history-status-filter"
          >
            <option value="all">Todos os status</option>
            <option value="nao_pago">Não pagos</option>
            <option value="pago">Pagos</option>
          </select>
          <div className="ml-auto flex gap-2 text-xs">
            <span className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded px-2 py-1">
              Pagos: <strong className="font-mono">{money(totalPaid)}</strong>
            </span>
            <span className="bg-amber-500/10 text-amber-300 border border-amber-500/30 rounded px-2 py-1">
              Em aberto: <strong className="font-mono">{money(totalOpen)}</strong>
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              Nenhum projeto encontrado.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 text-slate-400 text-xs uppercase sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2">Projeto</th>
                  <th className="text-left px-4 py-2">Cliente</th>
                  <th className="text-left px-4 py-2">Criado</th>
                  <th className="text-right px-4 py-2">Dubladores</th>
                  <th className="text-right px-4 py-2">Staff</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-center px-4 py-2">Status</th>
                  <th className="text-right px-4 py-2 w-32">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(p => {
                  const f = forecast[p.id] || {};
                  const paid = p.payment_status === 'pago';
                  return (
                    <tr
                      key={p.id}
                      className={`border-t border-slate-800 hover:bg-slate-800/40 transition-colors ${paid ? 'opacity-70' : ''}`}
                      data-testid={`project-history-row-${p.id}`}
                    >
                      <td className="px-4 py-2 text-white font-medium">{p.title}</td>
                      <td className="px-4 py-2 text-slate-300">{p.client || '—'}</td>
                      <td className="px-4 py-2 text-slate-400 whitespace-nowrap">
                        <Calendar className="h-3 w-3 inline mr-1 text-slate-500" />
                        {formatDate(p.created_at)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-teal-300">{money(f.dubladores_total)}</td>
                      <td className="px-4 py-2 text-right font-mono text-purple-300">{money(f.staff_total)}</td>
                      <td className="px-4 py-2 text-right font-mono font-bold text-white">{money(f.total)}</td>
                      <td className="px-4 py-2 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                          paid
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                        }`}>
                          {paid ? <><CheckCircle2 className="h-3 w-3" /> PAGO</> : 'Não pago'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setDetailsFor(p.id)}
                            className="text-xs px-2 py-1 rounded font-medium bg-slate-700 hover:bg-slate-600 text-slate-200 flex items-center gap-1"
                            data-testid={`project-history-details-${p.id}`}
                          >
                            <ExternalLink className="h-3 w-3" /> Ver detalhes
                          </button>
                          {canToggle && (
                            <button
                              onClick={() => toggleStatus(p)}
                              disabled={updating === p.id}
                              className={`text-xs px-2 py-1 rounded font-medium transition-colors flex items-center gap-1 ${
                                paid
                                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                                  : 'bg-emerald-600 hover:bg-emerald-500 text-white'
                              } disabled:opacity-50`}
                              data-testid={`project-history-toggle-${p.id}`}
                            >
                              {updating === p.id
                                ? <Loader2 className="h-3 w-3 animate-spin" />
                                : (paid ? <><RotateCcw className="h-3 w-3" /> Reabrir</> : <><CheckCircle2 className="h-3 w-3" /> Marcar pago</>)
                              }
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Inline details panel */}
        {detailsFor && forecast[detailsFor] && (
          <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6 z-10" data-testid="project-history-details-panel">
            <div className="w-full max-w-3xl bg-slate-800/70 border border-slate-700 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">
                    {forecast[detailsFor].project_title}
                  </h3>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Dubladores: {money(forecast[detailsFor].dubladores_total)} · Staff: {money(forecast[detailsFor].staff_total)} · <strong>Total: {money(forecast[detailsFor].total)}</strong>
                  </p>
                </div>
                <button
                  onClick={() => setDetailsFor(null)}
                  className="text-slate-400 hover:text-white text-sm px-3 py-1.5 bg-slate-700 rounded"
                  data-testid="project-history-details-close"
                >
                  Voltar
                </button>
              </div>
              <div className="space-y-4">
                <section>
                  <h4 className="text-xs uppercase tracking-wider text-teal-300 font-semibold mb-2">Dubladores</h4>
                  {(forecast[detailsFor].dubladores || []).length === 0 ? (
                    <p className="text-xs text-slate-500 italic">Nenhum dublador atribuído.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/60 text-slate-400 text-[10px] uppercase">
                        <tr>
                          <th className="text-left px-2 py-1.5">Personagem</th>
                          <th className="text-left px-2 py-1.5">Dublador</th>
                          <th className="text-right px-2 py-1.5">Falas</th>
                          <th className="text-right px-2 py-1.5">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast[detailsFor].dubladores.map((d, i) => (
                          <tr key={i} className="border-t border-slate-700/50">
                            <td className="px-2 py-1.5 text-white">{d.character_name}</td>
                            <td className="px-2 py-1.5 text-slate-300">{d.user_name}</td>
                            <td className="px-2 py-1.5 text-right text-slate-400 font-mono">{d.num_falas}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-400">{money(d.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
                <section>
                  <h4 className="text-xs uppercase tracking-wider text-purple-300 font-semibold mb-2">Equipe</h4>
                  {(forecast[detailsFor].staff || []).length === 0 ? (
                    <p className="text-xs text-slate-500 italic">Nenhum staff atribuído.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-800/60 text-slate-400 text-[10px] uppercase">
                        <tr>
                          <th className="text-left px-2 py-1.5">Nome</th>
                          <th className="text-left px-2 py-1.5">Função</th>
                          <th className="text-right px-2 py-1.5">Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecast[detailsFor].staff.map((s, i) => (
                          <tr key={i} className="border-t border-slate-700/50">
                            <td className="px-2 py-1.5 text-white">{s.user_name}</td>
                            <td className="px-2 py-1.5 text-slate-300">{s.role_label}</td>
                            <td className="px-2 py-1.5 text-right font-mono text-emerald-400">{money(s.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectHistoryModal;
